import { createSelector } from "reselect";
import {
  DeviceMinimizedMap,
  FiltersType,
  IFiltersTypeMap,
  presenceStatuses,
  Resident,
  ResidentMap,
  ResidentSort,
  ResidentSortType,
} from ".";

import { Device, DeviceData, DeviceType } from "../devices";
import { getDevicesByIds, organizationHasWatch } from "../devices/selectors";
import {
  ActivityStatus,
  AppStateDevicesStatus,
  BedStatusValue,
  OccupancyStatus,
  OnOffStatus,
  PostureStatus,
  PostureStatusValue,
  PowerStatus,
  UserNearbyStatus,
  VITALS_LOW_RSSI,
  WhyNotActive,
  WifiStatus,
} from "../devicesStatuses";
import {
  AppStateResidentsStatus,
  ResidentBedStatus,
} from "../residentStatuses";
import {
  currentUserBaseNode,
  nodeById,
  organizationNodes,
  pathToNode,
  userMonitorNodeId,
  userSelectedENDNodeId,
  userSelectedNodeId,
} from "../organization/selectors";
import { IAppState } from "../types";
import { authReady } from "../userProfile/selectors";
import { NodeType, OrganizationNode } from "../organization";

export const sortResidents = (
  residents: Resident[],
  sort: ResidentSort,
  organizationNodes?: OrganizationNode[],
  wordForRoot?: string
): Resident[] => {
  const sortByNodePresence = (a: Resident, b: Resident): number => {
    if (a.nodePresence === undefined || b.nodePresence === undefined) return 0;
    if (
      a.nodePresence === presenceStatuses.discharged &&
      b.nodePresence !== presenceStatuses.discharged
    )
      return sort.direction === "ASC" ? 1 : -1;
    if (
      a.nodePresence !== presenceStatuses.discharged &&
      b.nodePresence === presenceStatuses.discharged
    ) {
      return sort.direction === "ASC" ? -1 : 1;
    }
    return 0;
  };

  const sortByName = (
    a: Resident,
    b: Resident,
    includeNodePresense = true
  ): number => {
    if (a.nodePresence === undefined || b.nodePresence === undefined) return 0;
    if (a.firstName === undefined || b.firstName === undefined) return 0;
    if (
      includeNodePresense &&
      a.nodePresence !== b.nodePresence &&
      (a.nodePresence === presenceStatuses.discharged ||
        b.nodePresence === presenceStatuses.discharged)
    )
      return sortByNodePresence(a, b);
    if (a?.firstName.toLowerCase() < b?.firstName.toLowerCase())
      return sort.direction === "ASC" ? -1 : 1;
    if (a?.firstName.toLowerCase() > b?.firstName.toLowerCase()) {
      return sort.direction === "ASC" ? 1 : -1;
    }
    if (a?.lastName.toLowerCase() < b?.lastName.toLowerCase())
      return sort.direction === "ASC" ? -1 : 1;
    if (a?.lastName.toLowerCase() > b?.lastName.toLowerCase()) {
      return sort.direction === "ASC" ? 1 : -1;
    }
    return 0;
  };

  const sortCondition = (a: Resident, b: Resident): number => {
    if (a.devicesMinimized === undefined || b.devicesMinimized === undefined)
      return 0;
    const relatedDevicesA = Object.values(a?.devicesMinimized).filter(
      (el) => el.type === DeviceType.vitals
    );
    const relatedDevicesB = Object.values(b?.devicesMinimized).filter(
      (el) => el.type === DeviceType.vitals
    );
    if (relatedDevicesA.length === 0 && relatedDevicesB.length === 0)
      return sortByName(a, b);
    if (relatedDevicesA.length === 0 && relatedDevicesB.length !== 0)
      return sort.direction === "ASC" ? 1 : -1;
    if (relatedDevicesA.length !== 0 && relatedDevicesB.length === 0)
      return sort.direction === "ASC" ? -1 : 1;
    // if (relatedDevicesB === undefined) return sort === 'DESC' ? -1 : 1;
    if (
      Number.parseInt(relatedDevicesA[0]?.room) <
      Number.parseInt(relatedDevicesB[0]?.room)
    ) {
      return sort.direction === "ASC" ? -1 : 1;
    }
    if (
      Number.parseInt(relatedDevicesA[0]?.room) >
      Number.parseInt(relatedDevicesB[0]?.room)
    ) {
      return sort.direction === "ASC" ? 1 : -1;
    }
    if (relatedDevicesA[0]?.room === relatedDevicesB[0]?.room) {
      if (
        relatedDevicesA[0]?.bed?.toLowerCase() <
        relatedDevicesB[0]?.bed?.toLowerCase()
      ) {
        return sort.direction === "ASC" ? -1 : 1;
      }
      if (
        relatedDevicesA[0]?.bed?.toLowerCase() >
        relatedDevicesB[0]?.bed?.toLowerCase()
      ) {
        return sort.direction === "ASC" ? 1 : -1;
      }
    }
    return sortByName(a, b);
  };
  const sortByBlock = (a: Resident, b: Resident): number => {
    if (a.nodeId === undefined || b.nodeId === undefined) return 0;
    const aBlock = nodeById(organizationNodes, a.nodeId);
    const bBlock = nodeById(organizationNodes, b.nodeId);
    const aTitle = aBlock.type === NodeType.Root ? wordForRoot : aBlock.title;
    const bTitle = bBlock.type === NodeType.Root ? wordForRoot : bBlock.title;

    if (aTitle?.toLowerCase() < bTitle?.toLowerCase()) {
      return sort.direction === "ASC" ? -1 : 1;
    }
    if (aTitle?.toLowerCase() > bTitle?.toLowerCase()) {
      return sort.direction === "ASC" ? 1 : -1;
    }
    return sortByName(a, b, false);
  };

  return residents.sort(
    sort.type === ResidentSortType.RoomBed
      ? sortCondition
      : sort.type === ResidentSortType.Block
      ? sortByBlock
      : sortByName
  );
};
const isNoWifi = (status: WifiStatus) => {
  const validWifi =
    status !== null && status !== undefined && Object.keys(status).length > 1;
  return !validWifi;
};

const isUnplugged = (status: PowerStatus, deviceType: DeviceType) => {
  //being unplagged for biopatch is normal
  if (deviceType === DeviceType.biopatch) return false;
  if (!status) return false;
  return status.connected !== undefined ? !status.connected : false;
};

const isBatteryLow = (power: PowerStatus) => {
  return power && power.batteryLevel >= 5 && power.batteryLevel < 30;
};

const isBatteryEmpty = (power: PowerStatus) => {
  return power && power.batteryLevel >= 0 && power.batteryLevel < 5;
};
export const residentsConvertToArray = createSelector(
  (state: IAppState): ResidentMap => state.residents.residents,
  (residents) => {
    return Object.keys(residents).map((key) => residents[key]);
  }
);

export const residentsSlicedBySelectedNode = createSelector(
  residentsConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (residents, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode) return residents;
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return residents.filter((resident: Resident) =>
      isResidentUnderNode(Number(resident.nodeId), userSelectedNodeId)
    );
  }
);

export const residentsSlicedBySelectedMonitorNode = createSelector(
  residentsConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userMonitorNodeId,
  currentUserBaseNode,
  (residents, organizationNodes, userMonitorNodeId, currentUserBaseNode) => {
    if (userMonitorNodeId === currentUserBaseNode)
      return residents.filter(
        (el) => el?.nodePresence !== presenceStatuses.discharged
      );
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return residents
      .filter((resident: Resident) =>
        isResidentUnderNode(resident.nodeId, userMonitorNodeId)
      )
      .filter((el) => el?.nodePresence !== presenceStatuses.discharged);
  }
);

export const residentsSlicedBySelectedNodeIDS = createSelector(
  residentsConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (residents, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return residents.map((el) => el.id);
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return residents
      .filter((resident: Resident) =>
        isResidentUnderNode(resident.nodeId, userSelectedNodeId)
      )
      .map((el) => el.id);
  }
);

export const residentsSlicedBySelectedENDNode = createSelector(
  residentsConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedENDNodeId,
  currentUserBaseNode,
  (residents, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode) return residents;
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return residents.filter((resident: Resident) =>
      isResidentUnderNode(resident.nodeId, userSelectedNodeId)
    );
  }
);

export const residentsFiltered = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (state: IAppState): boolean => state.residents.settings.showDischarged,
  (_: IAppState, residents: Resident[]) => residents,
  (_: IAppState, __: Resident[], filters: IFiltersTypeMap) => filters,
  (
    devices: DeviceData,
    deviceStatuses: AppStateDevicesStatus,
    residentStatuses: AppStateResidentsStatus,
    showDischarged: boolean,
    residents: Resident[],
    filters: IFiltersTypeMap
  ) => {
    return residents
      .filter((resident: Resident) =>
        showDischarged
          ? true
          : resident.nodePresence !== presenceStatuses.discharged
      )
      .filter(
        filterCondition(filters, devices, deviceStatuses, residentStatuses)
      );
  }
);

export const residentIDsSearchExact = createSelector(
  (residents: Resident[]) => residents,
  (_: Resident[], searchValue: string) => searchValue,
  (residents: Resident[], searchValue: string) => {
    if (searchValue === "") return residents.map((el) => el.id);
    const search = (a: Resident): boolean => {
      if (a.firstName && a.lastName)
        if (
          a.firstName.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
          a.lastName.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
        )
          return true;
    };
    return residents.filter(search).map((el) => el.id);
  }
);

export const residentsSortedByName = createSelector(
  (residents: Resident[]) => residents,
  (_: Resident[], order: string) => order,
  (residents: Resident[], order: string) => {
    if (order === undefined) return residents;
    const compare = (a: Resident, b: Resident): number => {
      if (
        a.devices === undefined ||
        a.devices[0] === undefined
        // ||
        // b.devices === undefined ||
        // b.devices[0] === undefined
      )
        return order === "ASC" ? -1 : 1;
      if (b.devices === undefined || b.devices[0] === undefined)
        return order === "ASC" ? 1 : -1;
      const relatedDevicesA = Object.values(a.devicesMinimized).filter(
        (el) => el.type === DeviceType.vitals
      );
      const relatedDevicesB = Object.values(b.devicesMinimized).filter(
        (el) => el.type === DeviceType.vitals
      );
      if (relatedDevicesA[0]?.room < relatedDevicesB[0]?.room) {
        return order === "ASC" ? -1 : 1;
      }
      if (relatedDevicesA[0]?.room > relatedDevicesB[0]?.room) {
        return order === "ASC" ? 1 : -1;
      }
      if (relatedDevicesA[0]?.room === relatedDevicesB[0].room) {
        if (relatedDevicesA[0]?.bed < relatedDevicesB[0]?.bed) {
          return order === "ASC" ? -1 : 1;
        }
        if (relatedDevicesA[0]?.bed > relatedDevicesB[0]?.bed) {
          return order === "ASC" ? 1 : -1;
        }
      }
      return 0;
    };
    return residents.sort(compare);
  }
);

export const residentsByIds = createSelector(
  (ids: string[]) => ids,
  (_: string[], state: IAppState): ResidentMap => state.residents.residents,
  (ids, residents) => {
    return ids.map((id) => residents[id]);
  }
);

export const getResidentsByIds = createSelector(
  (ids: string[]) => ids,
  (_: string[], residents: ResidentMap) => residents,
  (ids, residents) => {
    const result: Resident[] = [];
    ids.forEach((id) => {
      if (residents.hasOwnProperty(id)) result.push(residents[id]);
    });
    return result;
  }
);

export const residentsToDisplay = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  residentsConvertToArray,
  organizationNodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (state: IAppState): IFiltersTypeMap => state.residents.settings.filters,
  (state: IAppState): string => state.residents.settings.searchValue,
  (state: IAppState): ResidentSort => state.residents.settings.sort,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (state: IAppState): boolean => state.residents.settings.showDischarged,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (_: IAppState, wordForRoot: string): string => wordForRoot,
  (
    devices,
    residents,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    filters,
    searchValue,
    sort,
    deviceStatuses,
    showDischarged,
    residentStatuses,
    wordForRoot
  ) => {
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      if (userSelectedNodeId === currentUserBaseNode) return true;
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };

    return sortResidents(
      residents
        .filter((resident: Resident) =>
          isResidentUnderNode(resident.nodeId, userSelectedNodeId)
        )
        .filter((resident: Resident) =>
          showDischarged
            ? true
            : resident.nodePresence !== presenceStatuses.discharged
        )
        .filter(
          filterCondition(filters, devices, deviceStatuses, residentStatuses)
        ),

      sort,
      organizationNodes,
      wordForRoot
    );
  }
);

export const residentsOnNode = createSelector(
  residentsConvertToArray,
  organizationNodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (residents, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      if (userSelectedNodeId === currentUserBaseNode) return true;
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };

    return residents.filter((resident: Resident) =>
      isResidentUnderNode(resident.nodeId, userSelectedNodeId)
    );
  }
);

export const residentsSelectedCreator = () => {
  return createSelector(
    (state: IAppState): string[] => state.residents.settings.selectedResidents,
    (_: IAppState, residentId: string): string => residentId,
    (selectded, residentId) => {
      return selectded.indexOf(residentId) !== -1;
    }
  );
};

export const residentsCreator = () => {
  return createSelector(
    (state: IAppState): ResidentMap => state.residents.residents,
    (_: IAppState, residentId: string): string => residentId,
    (residents, residentId) => {
      return residents[residentId];
    }
  );
};

export const residentDevicesAfterGateCreator = () => {
  return createSelector(
    (state: IAppState): ResidentMap => state.residents.residents,
    organizationHasWatch,
    (_: IAppState, residentId: string): string => residentId,
    (residents, organizationHasWatch, residentId) => {
      const res = residents[residentId];
      if (!res) return {};
      if (organizationHasWatch) {
        return res.devicesMinimized;
      }
      const newMin = {};
      Object.keys(res.devicesMinimized).map((id) => {
        if (res.devicesMinimized[id].type !== DeviceType.watch)
          newMin[id] = res.devicesMinimized[id];
        return null;
      });
      return newMin as DeviceMinimizedMap;
    }
  );
};

export const isResidentPresent = createSelector(
  (state: IAppState): ResidentMap => state.residents.residents,
  (_: IAppState, residentId: string): string => residentId,
  (residents, residentId) => {
    const res = residents[residentId];
    if (res) {
      return (
        res.nodePresence !== presenceStatuses.discharged &&
        res.nodePresence !== presenceStatuses.away
      );
    }
    return false;
  }
);

export const residentIdByDevice = createSelector(
  (state: IAppState): ResidentMap => state.residents.residents,
  (_: IAppState, deviceId: string): string => deviceId,
  (residents, deviceId) => {
    let residentId = undefined;
    Object.values(residents).map((el) => {
      if (residentId !== undefined) return null;
      if (el.devices.findIndex((dev) => dev === deviceId) !== -1)
        residentId = el.id;
      return null;
    });
    return residentId;
  }
);

export const otherActiveDevices = createSelector(
  (state: IAppState): ResidentMap => state.residents.residents,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (_: IAppState, deadDeviceId: string): string => deadDeviceId,
  (_: IAppState, __: string, residentId: string): string => residentId,
  (residents, devicesStatuses, deadDeviceId, residentId) => {
    const resident = residents[residentId];
    if (resident.devices.length === 1) return [];
    const otherDevices = Object.keys(resident.devicesMinimized).filter(
      (key) => key !== deadDeviceId
    );
    const res = [];
    otherDevices.map((devId) => {
      if (devId) {
        const device = resident.devicesMinimized[devId];
        if (device.type === DeviceType.vitals) {
          const occup = devicesStatuses.occupancyStatuses[devId];
          if (
            occup &&
            (occup as OccupancyStatus)?.value === BedStatusValue.IN_BED
          )
            res.push(DeviceType.vitals);
        } else if (device.type === DeviceType.biopatch) {
          const posture = devicesStatuses.postureStatuses[devId];
          if (
            posture &&
            (posture as PostureStatus)?.value !== PostureStatusValue.UNATTACHED
          )
            return res.push(DeviceType.biopatch);
        } else if (device.type === DeviceType.watch) {
          const activityStatus: ActivityStatus = devicesStatuses
            .activityStatuses[devId] as ActivityStatus;
          if (activityStatus?.isActive) return res.push(DeviceType.watch);
        }
      }
      return null;
    });

    return res;
  }
);
export const residentsReady = createSelector(
  authReady,
  (state: IAppState): boolean => state.residents.isLoading,
  (state: IAppState): boolean => state.organization.isLoading,
  (state: IAppState): boolean => state.healthCheck.residentServiceError,
  (mainLoaded, isLoading, isLoadingOrg, residentServiceError) => {
    return !mainLoaded
      ? false
      : isLoading === false &&
          residentServiceError === false &&
          isLoadingOrg === false;
  }
);

export const isSelectionMode = createSelector(
  (state: IAppState): string[] => state.residents.settings.selectedResidents,
  (selectedResidents) => {
    return selectedResidents.length > 0;
  }
);

const filterCondition = (
  filters: IFiltersTypeMap,
  devices,
  devicesStatuses: AppStateDevicesStatus,
  residentStatuses: AppStateResidentsStatus,
  logic = "AND"
) => {
  const condition = (el: Resident) => {
    let meetAnycondition = false;
    let meetAndCondition = undefined;
    if (
      Object.values(filters).every((value) => {
        return !value;
      })
    )
      return true;
    const relatedDevices = getDevicesByIds(devices, el.devices);
    const someActive = relatedDevices.some(
      (el: Device) =>
        (devicesStatuses.activityStatuses[el.id] as ActivityStatus)?.isActive
    );
    if (filters[FiltersType.DEVICE_STATUS_ACTIVE])
      meetAnycondition = meetAnycondition || someActive;

    if (filters[FiltersType.DEVICE_STATUS_NOT_ACTIVE])
      meetAnycondition =
        meetAnycondition || (!someActive && relatedDevices.length > 0);

    if (
      filters[FiltersType.DEVICE_STATUS_ACTIVE] ||
      filters[FiltersType.DEVICE_STATUS_NOT_ACTIVE]
    )
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        (filters[FiltersType.DEVICE_STATUS_ACTIVE] &&
        filters[FiltersType.DEVICE_STATUS_NOT_ACTIVE]
          ? true
          : filters[FiltersType.DEVICE_STATUS_NOT_ACTIVE]
          ? !someActive && relatedDevices.length > 0
          : someActive);
    const noDevice =
      relatedDevices?.length === 0 ||
      relatedDevices === undefined ||
      el.devices === undefined;
    const hasVitals =
      relatedDevices !== undefined &&
      relatedDevices.some((el) => el?.type === DeviceType.vitals);
    const hasBiopatch =
      relatedDevices !== undefined &&
      relatedDevices.some((el) => el?.type === DeviceType.biopatch);
    const hasWatch =
      relatedDevices !== undefined &&
      relatedDevices.some((el) => el?.type === DeviceType.watch);
    if (filters[FiltersType.DEVICE_NO])
      meetAnycondition = meetAnycondition || noDevice;
    if (filters[FiltersType.DEVICE_VITALS])
      meetAnycondition = meetAnycondition || hasVitals;
    if (filters[FiltersType.DEVICE_BIOPATCH])
      meetAnycondition = meetAnycondition || hasBiopatch;
    if (filters[FiltersType.DEVICE_WATCH])
      meetAnycondition = meetAnycondition || hasWatch;
    if (
      filters[FiltersType.DEVICE_NO] ||
      filters[FiltersType.DEVICE_VITALS] ||
      filters[FiltersType.DEVICE_BIOPATCH] ||
      filters[FiltersType.DEVICE_WATCH]
    )
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        (filters[FiltersType.DEVICE_NO] &&
        filters[FiltersType.DEVICE_VITALS] &&
        filters[FiltersType.DEVICE_BIOPATCH]
          ? true
          : Object.keys(filters)
              .filter(
                (key) =>
                  filters[key] === true &&
                  [
                    FiltersType.DEVICE_NO,
                    FiltersType.DEVICE_VITALS,
                    FiltersType.DEVICE_BIOPATCH,
                    FiltersType.DEVICE_WATCH,
                  ].indexOf(key as FiltersType) !== -1
              )
              .findIndex(
                (filt) =>
                  (filt === FiltersType.DEVICE_NO && noDevice) ||
                  (filt === FiltersType.DEVICE_VITALS && hasVitals) ||
                  (filt === FiltersType.DEVICE_WATCH && hasWatch) ||
                  (filt === FiltersType.DEVICE_BIOPATCH && hasBiopatch)
              ) !== -1);
    if (filters[FiltersType.RESIDENTS_DISCHARGED])
      meetAnycondition =
        meetAnycondition || el.nodePresence === presenceStatuses.discharged;
    if (filters[FiltersType.RESIDENTS_AWAY])
      meetAnycondition =
        meetAnycondition || el.nodePresence === presenceStatuses.away;
    if (filters[FiltersType.RESIDENTS_ACTIVE])
      meetAnycondition =
        meetAnycondition || el.nodePresence === presenceStatuses.active;

    if (
      filters[FiltersType.RESIDENTS_DISCHARGED] ||
      filters[FiltersType.RESIDENTS_AWAY] ||
      filters[FiltersType.RESIDENTS_ACTIVE]
    )
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        (filters[FiltersType.RESIDENTS_DISCHARGED] &&
        filters[FiltersType.RESIDENTS_AWAY] &&
        filters[FiltersType.RESIDENTS_ACTIVE]
          ? true
          : Object.keys(filters)
              .filter(
                (key) =>
                  filters[key] === true &&
                  [
                    FiltersType.RESIDENTS_DISCHARGED,
                    FiltersType.RESIDENTS_AWAY,
                    FiltersType.RESIDENTS_ACTIVE,
                  ].indexOf(key as FiltersType) !== -1
              )
              .findIndex(
                (filt) =>
                  (filt === FiltersType.RESIDENTS_DISCHARGED &&
                    el.nodePresence === presenceStatuses.discharged) ||
                  (filt === FiltersType.RESIDENTS_AWAY &&
                    el.nodePresence === presenceStatuses.away) ||
                  (filt === FiltersType.RESIDENTS_ACTIVE &&
                    el.nodePresence === presenceStatuses.active)
              ) !== -1);
    if (hasVitals) {
      const vitalsId = relatedDevices.find(
        (el) => el?.type === DeviceType.vitals
      ).id;
      const powerVit = devicesStatuses.powerStatuses[vitalsId] as PowerStatus;
      const activityVit = devicesStatuses.activityStatuses[
        vitalsId
      ] as ActivityStatus;
      if (filters[FiltersType.UNPLUGGED]) {
        meetAnycondition =
          meetAnycondition ||
          isUnplugged(powerVit, DeviceType.vitals) ||
          activityVit?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY ||
          activityVit?.status === WhyNotActive.NO_BATTERY;
        meetAndCondition = meetAnycondition;
      }
      const wifi = devicesStatuses.wifiStatuses[vitalsId] as WifiStatus;
      if (filters[FiltersType.LOW_WIFI]) {
        meetAnycondition =
          meetAnycondition || (wifi as WifiStatus)?.rssiDbm <= VITALS_LOW_RSSI;
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.NO_WIFI]) {
        meetAnycondition =
          meetAnycondition ||
          (isNoWifi(wifi) && activityVit?.status === WhyNotActive.NO_WIFI);
        meetAndCondition = meetAnycondition;
      }
      const onoff = devicesStatuses.onOffStatuses[vitalsId] as OnOffStatus;
      if (filters[FiltersType.TURN_OFF]) {
        meetAnycondition = meetAnycondition || !onoff?.isOn;
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.BatteryLevelLow]) {
        meetAnycondition = meetAnycondition || isBatteryLow(powerVit);
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.BatteryLevelEmpty]) {
        meetAnycondition =
          meetAnycondition ||
          isBatteryEmpty(powerVit) ||
          activityVit?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY ||
          activityVit?.status === WhyNotActive.NO_BATTERY;
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.MAINTENANCE]) {
        meetAnycondition = meetAnycondition || (powerVit && powerVit?.failure);
        meetAndCondition = meetAnycondition;
      }
      const userNeaby = devicesStatuses.userNearbyStatuses[
        vitalsId
      ] as UserNearbyStatus;
      if (userNeaby?.nearBed?.length > 0) {
        const occupancyStatus = devicesStatuses.occupancyStatuses[
          vitalsId
        ] as OccupancyStatus;
        const bedStatus = residentStatuses.bedStatuses[
          el.id
        ] as ResidentBedStatus;
        const status = occupancyStatus
          ? { ...bedStatus, value: occupancyStatus.value }
          : bedStatus;
        if (!status || status?.value === BedStatusValue.INIT) return;
        if (
          filters[FiltersType.USER_NEARBY_IN_BED] &&
          filters[FiltersType.USER_NEARBY_OUT_BED]
        )
          meetAnycondition = meetAnycondition || true;
        if (filters[FiltersType.USER_NEARBY_IN_BED]) {
          meetAnycondition =
            meetAnycondition || status?.value === BedStatusValue.IN_BED;
          meetAndCondition = meetAnycondition;
        } else if (filters[FiltersType.USER_NEARBY_OUT_BED]) {
          meetAnycondition =
            meetAnycondition || status?.value === BedStatusValue.OUT_OF_BED;
          meetAndCondition = meetAnycondition;
        }
      }
      if (userNeaby?.nearRoom?.length > 0) {
        if (filters[FiltersType.USER_NEARBY_ROOM]) {
          meetAnycondition = meetAnycondition || true;
          meetAndCondition = meetAnycondition;
        }
      }
    }
    if (hasBiopatch) {
      const biopatchId = relatedDevices.find(
        (el) => el?.type === DeviceType.biopatch
      ).id;
      const activityBio = devicesStatuses.activityStatuses[
        biopatchId
      ] as ActivityStatus;
      const powerBio = devicesStatuses.powerStatuses[biopatchId] as PowerStatus;
      const badBlue =
        ((activityBio === undefined || !activityBio?.isActive) &&
          (powerBio === undefined || powerBio?.batteryLevel >= 0)) ||
        (!activityBio?.isActive &&
          activityBio?.status === WhyNotActive.NO_BLUETOOTH);
      if (filters[FiltersType.NO_BLUETOOTH]) {
        meetAnycondition = meetAnycondition || badBlue;
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.BatteryLevelLow]) {
        meetAnycondition = meetAnycondition || isBatteryLow(powerBio);
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.BatteryLevelEmpty]) {
        meetAnycondition =
          meetAnycondition || (isBatteryEmpty(powerBio) && !badBlue);
        meetAndCondition = meetAnycondition;
      }
      if (filters[FiltersType.MAINTENANCE]) {
        meetAnycondition = meetAnycondition || (powerBio && powerBio?.failure);
        meetAndCondition = meetAnycondition;
      }
    }

    return logic === "OR" ? meetAnycondition : meetAndCondition;
  };
  return condition;
};

export const samePresenceStatus = createSelector(
  (state: IAppState): string[] => state.residents.settings.selectedResidents,
  (state: IAppState): ResidentMap => state.residents.residents,
  (
    selectedResidents: any[],
    residents: { [x: string]: { nodePresence: any } }
  ) => {
    const firstStat = residents[selectedResidents[0]]?.nodePresence;
    const same = selectedResidents.every(
      (el) => residents[el].nodePresence === firstStat
    );
    return same ? firstStat : undefined;
  }
);

export const getResidentsByFilterTypesCreator = () => {
  return createSelector(
    residentsSlicedBySelectedNode,
    (state: IAppState): DeviceData => state.devices.devices,
    (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
    (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
    (state: IAppState): boolean => state.residents.settings.showDischarged,
    (residents, devices, devicesStatuses, residentStatuses, showDischarged) => {
      if (
        !residents ||
        Object.keys(residents).length === 0 ||
        residents?.length === 0
      )
        return {};

      const byFilters = {};
      Object.values(FiltersType).map((el) => (byFilters[el] = 0));
      residents
        .filter((resident: Resident) =>
          showDischarged
            ? true
            : resident.nodePresence !== presenceStatuses.discharged
        )
        .map((el: Resident) => {
          if (el.nodePresence === presenceStatuses.active)
            byFilters[FiltersType.RESIDENTS_ACTIVE] = byFilters[
              FiltersType.RESIDENTS_ACTIVE
            ]
              ? byFilters[FiltersType.RESIDENTS_ACTIVE] + 1
              : 1;
          else if (el.nodePresence === presenceStatuses.away)
            byFilters[FiltersType.RESIDENTS_AWAY] = byFilters[
              FiltersType.RESIDENTS_AWAY
            ]
              ? byFilters[FiltersType.RESIDENTS_AWAY] + 1
              : 1;
          else
            byFilters[FiltersType.RESIDENTS_DISCHARGED] = byFilters[
              FiltersType.RESIDENTS_DISCHARGED
            ]
              ? byFilters[FiltersType.RESIDENTS_DISCHARGED] + 1
              : 1;
          const relatedDevices = getDevicesByIds(devices, el.devices);
          if (relatedDevices.length === 0)
            byFilters[FiltersType.DEVICE_NO] = byFilters[FiltersType.DEVICE_NO]
              ? byFilters[FiltersType.DEVICE_NO] + 1
              : 1;
          else {
            const someActive = relatedDevices.some(
              (el: Device) =>
                (devicesStatuses.activityStatuses[el.id] as ActivityStatus)
                  ?.isActive
            );
            if (someActive)
              byFilters[FiltersType.DEVICE_STATUS_ACTIVE] = byFilters[
                FiltersType.DEVICE_STATUS_ACTIVE
              ]
                ? byFilters[FiltersType.DEVICE_STATUS_ACTIVE] + 1
                : 1;
            else
              byFilters[FiltersType.DEVICE_STATUS_NOT_ACTIVE] = byFilters[
                FiltersType.DEVICE_STATUS_NOT_ACTIVE
              ]
                ? byFilters[FiltersType.DEVICE_STATUS_NOT_ACTIVE] + 1
                : 1;
            const hasVitals =
              relatedDevices !== undefined &&
              relatedDevices.findIndex(
                (el) => el?.type === DeviceType.vitals
              ) !== -1;
            const hasBiopatch =
              relatedDevices !== undefined &&
              relatedDevices.findIndex(
                (el) => el?.type === DeviceType.biopatch
              ) !== -1;
            const hasWatch =
              relatedDevices !== undefined &&
              relatedDevices.findIndex(
                (el) => el?.type === DeviceType.watch
              ) !== -1;
            if (hasVitals)
              byFilters[FiltersType.DEVICE_VITALS] = byFilters[
                FiltersType.DEVICE_VITALS
              ]
                ? byFilters[FiltersType.DEVICE_VITALS] + 1
                : 1;
            if (hasBiopatch)
              byFilters[FiltersType.DEVICE_BIOPATCH] = byFilters[
                FiltersType.DEVICE_BIOPATCH
              ]
                ? byFilters[FiltersType.DEVICE_BIOPATCH] + 1
                : 1;
            if (hasWatch)
              byFilters[FiltersType.DEVICE_WATCH] = byFilters[
                FiltersType.DEVICE_WATCH
              ]
                ? byFilters[FiltersType.DEVICE_WATCH] + 1
                : 1;
            if (hasBiopatch) {
              const biopatchId = relatedDevices.find(
                (el) => el?.type === DeviceType.biopatch
              ).id;
              const activityBio = devicesStatuses.activityStatuses[
                biopatchId
              ] as ActivityStatus;
              const powerBio = devicesStatuses.powerStatuses[
                biopatchId
              ] as PowerStatus;
              const badBlue =
                ((activityBio === undefined || !activityBio?.isActive) &&
                  (powerBio === undefined || powerBio?.batteryLevel >= 0)) ||
                (!activityBio?.isActive &&
                  activityBio?.status === WhyNotActive.NO_BLUETOOTH);
              if (badBlue) {
                byFilters[FiltersType.NO_BLUETOOTH] =
                  byFilters[FiltersType.NO_BLUETOOTH] + 1;
              }
              if (isBatteryLow(powerBio) && !badBlue) {
                byFilters[FiltersType.BatteryLevelLow] =
                  byFilters[FiltersType.BatteryLevelLow] + 1;
              }
              if (isBatteryEmpty(powerBio) && !badBlue) {
                byFilters[FiltersType.BatteryLevelEmpty] =
                  byFilters[FiltersType.BatteryLevelEmpty] + 1;
              }
              if (powerBio && powerBio?.failure)
                byFilters[FiltersType.MAINTENANCE] =
                  byFilters[FiltersType.MAINTENANCE] + 1;
            }
            if (hasVitals) {
              const vitalsId = relatedDevices.find(
                (el) => el?.type === DeviceType.vitals
              ).id;
              const powerVit = devicesStatuses.powerStatuses[
                vitalsId
              ] as PowerStatus;
              const activityVit = devicesStatuses.activityStatuses[
                vitalsId
              ] as ActivityStatus;
              if (isBatteryLow(powerVit)) {
                byFilters[FiltersType.BatteryLevelLow] =
                  byFilters[FiltersType.BatteryLevelLow] + 1;
              }
              if (
                isBatteryEmpty(powerVit) ||
                activityVit?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY ||
                activityVit?.status === WhyNotActive.NO_BATTERY
              ) {
                byFilters[FiltersType.BatteryLevelEmpty] =
                  byFilters[FiltersType.BatteryLevelEmpty] + 1;
              }
              if (powerVit && powerVit?.failure)
                byFilters[FiltersType.MAINTENANCE] =
                  byFilters[FiltersType.MAINTENANCE] + 1;

              const onoff = devicesStatuses.onOffStatuses[
                vitalsId
              ] as OnOffStatus;
              if (!onoff?.isOn)
                byFilters[FiltersType.TURN_OFF] =
                  byFilters[FiltersType.TURN_OFF] + 1;
              if (
                isUnplugged(powerVit, DeviceType.vitals) ||
                activityVit?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY ||
                activityVit?.status === WhyNotActive.NO_BATTERY
              )
                byFilters[FiltersType.UNPLUGGED] =
                  byFilters[FiltersType.UNPLUGGED] + 1;
              const wifi = devicesStatuses.wifiStatuses[vitalsId] as WifiStatus;
              if (
                isNoWifi(wifi) &&
                activityVit?.status === WhyNotActive.NO_WIFI
              )
                byFilters[FiltersType.NO_WIFI] =
                  byFilters[FiltersType.NO_WIFI] + 1;

              if ((wifi as WifiStatus)?.rssiDbm <= VITALS_LOW_RSSI)
                byFilters[FiltersType.LOW_WIFI] =
                  byFilters[FiltersType.LOW_WIFI] + 1;

              const userNeaby = devicesStatuses.userNearbyStatuses[
                vitalsId
              ] as UserNearbyStatus;

              if (userNeaby?.nearBed?.length > 0) {
                const occupancyStatus = devicesStatuses.occupancyStatuses[
                  vitalsId
                ] as OccupancyStatus;
                const bedStatus = residentStatuses.bedStatuses[
                  el.id
                ] as ResidentBedStatus;
                const status = occupancyStatus
                  ? { ...bedStatus, value: occupancyStatus.value }
                  : bedStatus;
                if (!status || status?.value === BedStatusValue.INIT)
                  return null;
                if (status.value === BedStatusValue.IN_BED)
                  byFilters[FiltersType.USER_NEARBY_IN_BED] =
                    byFilters[FiltersType.USER_NEARBY_IN_BED] + 1;
                else if (status.value === BedStatusValue.OUT_OF_BED)
                  byFilters[FiltersType.USER_NEARBY_OUT_BED] =
                    byFilters[FiltersType.USER_NEARBY_OUT_BED] + 1;
              }
              if (userNeaby?.nearRoom?.length > 0) {
                byFilters[FiltersType.USER_NEARBY_ROOM] =
                  byFilters[FiltersType.USER_NEARBY_ROOM] + 1;
              }
            }
          }
          return null;
        });

      return byFilters;
    }
  );
};
